import React, { useEffect, useState } from 'react'
import { RouteComponentProps, Router } from '@reach/router'
import { NewsWall } from '../pages/newsWall'
import {
  ROUTE_CONFIGURATION,
  ROUTE_COOKIES_POLICY,
  ROUTE_DASHBOARD_ID,
  ROUTE_FORM_GENERATOR,
  ROUTE_FORM_GENERATOR_ASSIGNTO,
  ROUTE_FORM_GENERATOR_FORM_CREATE,
  ROUTE_FORM_GENERATOR_FORM_EDIT,
  ROUTE_FORM_GENERATOR_FORM_RESULTS,
  ROUTE_FORM_GENERATOR_USERS,
  ROUTE_NEWS_WALL,
  ROUTE_NOTIFICATIONS_CONFIG,
  ROUTE_PATIENT_FORMS,
  ROUTE_PATIENT_FORMS_VIEW,
  ROUTE_PATIENTS,
  ROUTE_PRIVACY_POLICY,
  ROUTE_ROLE_PERMISSIONS,
  ROUTE_PROTOCOLS,
  ROUTE_PROTOCOL_DETAIL,
  ROUTE_PROTOCOL_NEW,
  ROUTE_STATISTICS,
  ROUTE_STATISTICS_ID,
  ROUTE_USER_FORMS,
  ROUTE_USER_PROFILE,
  ROUTE_USER_DETAIL,
  ROUTE_USERS_CREATE,
  ROUTE_USERS_CREATE_ID,
  // ROUTE_RESOURCES,
  // ROUTE_RESOURCES_ID,
  // ROUTE_RESOURCES_SHARED_WITH_ME,
  // ROUTE_RESOURCES_SHARED_WITH_ME_ID,
  // ROUTE_RESOURCES_FORM_EDIT,
} from './routes-constants'
import { FormGenerator } from '../pages/form-generator'
import { FormGeneratorTable } from '../pages/form-generator-table'
import {
  CONFIGURATION_TITLE,
  COOKIES_POLICY_TITLE,
  CREATE_PATIENT_TITLE,
  DASHBOARD_TITLE,
  EDIT_PATIENT_TITLE,
  FORM_GENERATOR_ASSIGNTO_TITLE,
  FORM_GENERATOR_EDITOR_TITLE,
  FORM_GENERATOR_FORM_CREATE_TITLE,
  FORM_GENERATOR_RESULTS_TITLE,
  FORM_GENERATOR_TITLE,
  FORM_GENERATOR_USERS_TITLE,
  FORMS_TITLE,
  NEWSWALL_TITLE,
  PATIENT_FORM_VIEW_TITLE,
  PATIENTS_FORMS_TITLE,
  PRIVACY_POLICY_TITLE,
  RESOURCES_NOTIFICATIONS_CONFIG_TITLE,
  ROLE_PERMISSIONS_TITLE,
  PROTOCOLS_TITLE,
  PROTOCOL_DETAIL_TITLE,
  STATISTICS_TITLE,
  USER_PROFILE_TITLE,
  USERS_TITLE,
  // RESOURCES_TITLE,
  // RESOURCES_SHARED_WITH_ME_TITLE,
} from './title-constants'
import { FormGeneratorResults } from '../pages/form-generator-results'
import { Configuration } from '../pages/configuration'
import { PatientFormsTable } from '../pages/patient-forms-list'
import { PatientFormView } from '../pages/patient-forms-view'
import { NotificationsConfig } from '../pages/notifications-config/NotificationsConfig'
import { RolePermissions } from '../pages/role-permissions'
import { ICircleService } from '../modules/users/services/CircleService'
import { User } from '../modules/users/models/User'
import { EditParticipant, UserDetail } from '../pages/users'
import { FormGeneratorUsers } from '../pages/form-generator-users'
import { UserProfile } from '../pages/user-profile'
import { getUserContainer } from '../container/user-module'
import { CIRCLE_SERVICE_KEY } from '../modules/users/container'
import { UsersTable } from '../pages/users-table'
import { Statistics } from '../pages/statistics'
import { Protocols, ProtocolDetailPage } from '../pages/protocols'
import { Dashboard } from '../pages/dashboard'
import { FormGeneratorAssignTo } from 'pages/form-generator-assignTo'
import { PrivacyPolicy } from '../pages/privacy-policy'
import { CookiesPolicy } from '../pages/cookies-policy'
import { useUserRoles } from '../modules/users/hooks/useUserRoles'
import { ProtectedRoute } from './ProtectedRoute'
// import { Resources } from '../pages/resources'
// import { ResourcesForm } from '../pages/resources-form'

export type RouteProps = {
  title?: string
  subtitle?: string
  id?: string
} & RouteComponentProps

type UserRolesType = ReturnType<typeof useUserRoles>

const circleService = getUserContainer().get<ICircleService>(CIRCLE_SERVICE_KEY)

export const AppRouter: React.FC = () => {
  const [circle, setCircle] = useState<User | undefined>(circleService.getActive())

  useEffect(() => {
    circleService.getActiveObservable().subscribe((c) => setCircle(c))
  }, [circle])

  return (
    <Router component="div">
      <PrivacyPolicy path={ROUTE_PRIVACY_POLICY} title={PRIVACY_POLICY_TITLE} />
      <CookiesPolicy path={ROUTE_COOKIES_POLICY} title={COOKIES_POLICY_TITLE} />

      <ProtectedRoute
        path={ROUTE_NEWS_WALL}
        component={NewsWall}
        title={NEWSWALL_TITLE}
        canAccess={(r: UserRolesType) => r.canAccessHome}
      />

      <ProtectedRoute
        path={ROUTE_USER_PROFILE}
        component={UserProfile}
        title={USER_PROFILE_TITLE}
        canAccess={(r: UserRolesType) => r.canChangeOwnPassword}
      />

      <ProtectedRoute
        path={ROUTE_PATIENTS}
        component={UsersTable}
        title={USERS_TITLE}
        canAccess={(r: UserRolesType) => r.canViewPatients}
      />

      <ProtectedRoute
        path={ROUTE_USERS_CREATE}
        component={EditParticipant}
        title={CREATE_PATIENT_TITLE}
        canAccess={(r: UserRolesType) => r.canViewPatients}
      />

      <ProtectedRoute
        path={ROUTE_USERS_CREATE_ID}
        component={EditParticipant}
        title={EDIT_PATIENT_TITLE}
        canAccess={(r: UserRolesType) => r.canViewPatients}
      />

      <ProtectedRoute
        path={ROUTE_USER_DETAIL}
        component={UserDetail}
        title={USER_PROFILE_TITLE}
        canAccess={(r: UserRolesType) => r.canViewPatients}
      />

      <ProtectedRoute
        path={ROUTE_DASHBOARD_ID}
        component={Dashboard}
        title={DASHBOARD_TITLE}
        canAccess={(r: UserRolesType) => r.canViewPatients}
      />

      <ProtectedRoute
        path={ROUTE_FORM_GENERATOR}
        component={FormGeneratorTable}
        title={FORM_GENERATOR_TITLE}
        canAccess={(r: UserRolesType) => r.canAccessForms}
      />

      <ProtectedRoute
        path={ROUTE_FORM_GENERATOR_FORM_EDIT}
        component={FormGenerator}
        title={FORM_GENERATOR_EDITOR_TITLE}
        canAccess={(r: UserRolesType) => r.canAccessForms}
      />

      <ProtectedRoute
        path={ROUTE_FORM_GENERATOR_FORM_CREATE}
        component={FormGenerator}
        title={FORM_GENERATOR_FORM_CREATE_TITLE}
        canAccess={(r: UserRolesType) => r.canCreateForms}
      />

      <ProtectedRoute
        path={ROUTE_FORM_GENERATOR_FORM_RESULTS}
        component={FormGeneratorResults}
        title={FORM_GENERATOR_RESULTS_TITLE}
        canAccess={(r: UserRolesType) => r.canAccessForms}
      />

      <ProtectedRoute
        path={ROUTE_FORM_GENERATOR_USERS}
        component={FormGeneratorUsers}
        title={FORM_GENERATOR_USERS_TITLE}
        canAccess={(r: UserRolesType) => r.canCreateForms}
      />

      <ProtectedRoute
        path={ROUTE_FORM_GENERATOR_ASSIGNTO}
        component={FormGeneratorAssignTo}
        title={FORM_GENERATOR_ASSIGNTO_TITLE}
        canAccess={(r: UserRolesType) => r.canCreateForms}
      />

      <ProtectedRoute
        path={ROUTE_PATIENT_FORMS}
        component={PatientFormsTable}
        title={PATIENTS_FORMS_TITLE}
        canAccess={(r: UserRolesType) => r.canAccessForms}
      />

      <ProtectedRoute
        path={ROUTE_PATIENT_FORMS_VIEW}
        component={PatientFormView}
        title={PATIENT_FORM_VIEW_TITLE}
        canAccess={(r: UserRolesType) => r.canAccessForms}
      />

      <ProtectedRoute
        path={ROUTE_USER_FORMS}
        component={PatientFormsTable}
        title={FORMS_TITLE}
        canAccess={(r: UserRolesType) => r.canAccessForms}
      />

      {/* #3188 and #3196: protocol management and rule editor */}
      <ProtectedRoute
        path={ROUTE_PROTOCOLS}
        component={Protocols}
        title={PROTOCOLS_TITLE}
        canAccess={(r: UserRolesType) => r.canViewProtocols}
      />

      <ProtectedRoute
        path={ROUTE_PROTOCOL_NEW}
        component={ProtocolDetailPage}
        title={PROTOCOL_DETAIL_TITLE}
        canAccess={(r: UserRolesType) => r.canManageProtocols}
      />

      <ProtectedRoute
        path={ROUTE_PROTOCOL_DETAIL}
        component={ProtocolDetailPage}
        title={PROTOCOL_DETAIL_TITLE}
        canAccess={(r: UserRolesType) => r.canViewProtocols}
      />

      <ProtectedRoute
        path={ROUTE_STATISTICS}
        component={Statistics}
        title={STATISTICS_TITLE}
        canAccess={(r: UserRolesType) => r.canViewOwnStats}
      />

      <ProtectedRoute
        path={ROUTE_STATISTICS_ID}
        component={Statistics}
        title={STATISTICS_TITLE}
        canAccess={(r: UserRolesType) => false}
      />

      <ProtectedRoute
        path={ROUTE_CONFIGURATION}
        component={Configuration}
        title={CONFIGURATION_TITLE}
        canAccess={(r: UserRolesType) => r.isAdmin || r.isMed}
      />

      <ProtectedRoute
        path={ROUTE_ROLE_PERMISSIONS}
        component={RolePermissions}
        title={ROLE_PERMISSIONS_TITLE}
        canAccess={(r: UserRolesType) => r.isAdmin}
      />

      <ProtectedRoute
        path={ROUTE_NOTIFICATIONS_CONFIG}
        component={NotificationsConfig}
        title={RESOURCES_NOTIFICATIONS_CONFIG_TITLE}
        canAccess={(r: UserRolesType) => r.isAdmin || r.isMed}
      />

      {/*
       */}

      {/*
       */}

      {/*
      <ProtectedRoute
        path={ROUTE_RESOURCES}
        component={Resources}
        title={RESOURCES_TITLE}
        canAccess={(r: UserRolesType) => r.canAccessResources}
      />
      <ProtectedRoute
        path={ROUTE_RESOURCES_ID}
        component={Resources}
        title={RESOURCES_TITLE}
        canAccess={(r: UserRolesType) => r.canAccessResources}
      />
      <ProtectedRoute
        path={ROUTE_RESOURCES_SHARED_WITH_ME}
        component={Resources}
        title={RESOURCES_SHARED_WITH_ME_TITLE}
        canAccess={(r: UserRolesType) => r.canAccessResources}
      />
      <ProtectedRoute
        path={ROUTE_RESOURCES_SHARED_WITH_ME_ID}
        component={Resources}
        title={RESOURCES_SHARED_WITH_ME_TITLE}
        canAccess={(r: UserRolesType) => r.canAccessResources}
      />
      <ProtectedRoute
        path={ROUTE_RESOURCES_FORM_EDIT}
        component={ResourcesForm}
        title={RESOURCES_TITLE}
        canAccess={(r: UserRolesType) => r.canAccessResources}
      />
      */}
    </Router>
  )
}
